import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useChat } from "../context/ChatContext";
import { useLanguage } from "../context/LanguageContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Send,
  Paperclip,
  Plus,
  LogOut,
  Menu,
  X,
  Sparkles,
  ScanFace,
  Scissors,
  Droplets,
  MessageSquare,
  UserMinus,
  CreditCard,
  ImageIcon,
  MessageCircle,
} from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { LegalPopover } from "../components/LegalModal";
import CameraModal from "../components/CameraModal";

export default function ChatPage({ onLogoClick, showCameraOnMount = false, onCameraDone }) {
  const { user, logout, deleteAccount, credits, needsPayment, createCheckout, fetchCredits } = useAuth();
  const [deleteStep, setDeleteStep] = useState(0); // 0=hidden, 1=confirm
  const [showPaywall, setShowPaywall] = useState(false);
  const [showCamera, setShowCamera] = useState(showCameraOnMount);
  const {
    conversations,
    activeConversation,
    setActiveConversation,
    messages,
    isStreaming,
    streamingContent,
    streamingMeta,
    lastImageUrl,
    loadConversations,
    createConversation,
    loadMessages,
    sendMessage,
  } = useChat();
  const { t } = useLanguage();

  const [input, setInput] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const sendingRef = useRef(false);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingContent, isStreaming]);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 128) + "px";
    }
  }, [input]);

  const handleSend = async () => {
    if (sendingRef.current) return;
    if (!input.trim() && !selectedFiles.length) return;
    if (selectedFiles.some((f) => f.data === null)) return;
    if (needsPayment) { setShowPaywall(true); return; }
    sendingRef.current = true;

    const text = input.trim();
    const hasImage = selectedFiles.length > 0 || lastImageUrl;
    const action = hasImage ? "edit_image" : "text_to_image";
    const files = selectedFiles.length ? selectedFiles : null;

    // Revoke object URLs to free memory
    selectedFiles.forEach((f) => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); });
    setInput("");
    setSelectedFiles([]);

    try {
      await sendMessage(text, action, files);
      fetchCredits();
    } catch (e) {
      if (e.message === "NO_CREDITS") {
        setShowPaywall(true);
        fetchCredits();
      }
    } finally {
      sendingRef.current = false;
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    files.forEach((file) => {
      const previewUrl = URL.createObjectURL(file);
      const fileEntry = {
        name: file.name,
        type: file.type,
        fileType: file.type.startsWith("image/") ? "image" : "other",
        data: null,
        size: file.size,
        previewUrl,
      };
      setSelectedFiles((prev) => [...prev, fileEntry]);
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        setSelectedFiles((prev) =>
          prev.map((f) => (f.previewUrl === previewUrl ? { ...f, data: base64 } : f))
        );
      };
      reader.readAsDataURL(file);
    });
    // Reset input so same file can be selected again
    e.target.value = "";
  };

  const selectConversation = async (conv) => {
    setActiveConversation(conv);
    await loadMessages(conv.id);
    setSidebarOpen(false);
  };

  const suggestions = [
    { icon: ScanFace, text: t("chat_suggest_1"), color: "text-accent-dark" },
    { icon: Scissors, text: t("chat_suggest_2"), color: "text-accent-dark" },
    { icon: Droplets, text: t("chat_suggest_3"), color: "text-accent-dark" },
    { icon: Sparkles, text: t("chat_suggest_4"), color: "text-accent-dark" },
  ];

  return (
    <div className="h-screen flex bg-white font-sans antialiased">
      {/* Sidebar Overlay (mobile) */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-20 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0 fixed md:relative z-30 w-72 h-full bg-surface-warm border-r border-surface-border flex flex-col transition-transform duration-200`}
      >
        {/* Sidebar Header */}
        <div className="p-5 border-b border-surface-border">
          <div className="flex items-center justify-between mb-4">
            <button onClick={onLogoClick} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <img src="/logo.png" alt="Beauly" className="h-8 object-contain" />
              <span className="text-xl font-display text-primary">Beauly</span>
            </button>
            <button onClick={() => setSidebarOpen(false)} className="md:hidden text-text-muted hover:text-primary transition-colors">
              <X size={20} />
            </button>
          </div>
          <button
            onClick={async () => {
              await createConversation();
              setSidebarOpen(false);
            }}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
          >
            <Plus size={16} /> {t("chat_new")}
          </button>
        </div>

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto py-2 px-3">
          {conversations.length === 0 && (
            <div className="text-center py-8">
              <MessageSquare size={24} className="mx-auto text-primary-300 mb-2" />
              <p className="text-text-muted text-xs">{t("chat_no_chats")}</p>
            </div>
          )}
          {conversations.map((conv) => (
            <button
              key={conv.id}
              onClick={() => selectConversation(conv)}
              className={`w-full text-left px-3 py-2.5 rounded-lg mb-0.5 text-sm transition-colors ${
                activeConversation?.id === conv.id
                  ? "bg-primary-50 text-primary font-medium"
                  : "text-text-secondary hover:bg-surface"
              }`}
            >
              <span className="block truncate">{conv.title || t("chat_new_conv")}</span>
            </button>
          ))}
        </div>

        {/* Credits */}
        {credits && (
          <div className="px-4 pt-3 pb-1 border-t border-surface-border">
            <div className="flex items-center justify-between text-xs text-text-muted mb-1.5">
              <span className="flex items-center gap-1.5"><MessageCircle size={12} /> {credits.credits_messages} {t("credits_messages") || "Messages"}</span>
              <span className="flex items-center gap-1.5"><ImageIcon size={12} /> {credits.credits_images} {t("credits_images") || "Images"}</span>
            </div>
            {needsPayment && (
              <button
                onClick={createCheckout}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-accent text-white rounded-lg text-xs font-medium hover:bg-accent-hover transition-colors"
              >
                <CreditCard size={12} /> {t("credits_buy") || "Buy credits"}
              </button>
            )}
          </div>
        )}

        {/* User Info */}
        <div className="p-4 border-t border-surface-border">
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-muted truncate max-w-[140px]">{user?.name || user?.email}</span>
            <div className="flex items-center gap-2">
              <LegalPopover />
              <button
                onClick={() => setDeleteStep(1)}
                className="text-text-muted hover:text-red-500 transition-colors p-1"
                title={t("chat_delete_account") || "Account löschen"}
              >
                <UserMinus size={15} />
              </button>
              <LanguageSwitcher />
              <button
                onClick={logout}
                className="text-text-muted hover:text-primary transition-colors p-1"
                title={t("chat_logout")}
              >
                <LogOut size={15} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="h-14 flex items-center px-5 border-b border-surface-border bg-white flex-shrink-0">
          <button onClick={() => setSidebarOpen(true)} className="md:hidden mr-3 text-text-muted hover:text-primary transition-colors">
            <Menu size={20} />
          </button>
          <span className="font-medium text-primary text-sm truncate">
            {activeConversation?.title || t("chat_default_title")}
          </span>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 py-6">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
                <div className="w-14 h-14 rounded-2xl bg-primary-50 flex items-center justify-center mb-6">
                  <Sparkles size={24} className="text-accent-dark" strokeWidth={1.5} />
                </div>
                <h2 className="text-2xl font-display text-primary mb-2">
                  {t("chat_welcome")}
                </h2>
                <p className="text-text-muted max-w-md mb-8 leading-relaxed">
                  {t("chat_welcome_desc")}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-lg">
                  {suggestions.map((s) => (
                    <button
                      key={s.text}
                      onClick={() => setInput(s.text)}
                      className="flex items-center gap-3 px-4 py-3.5 bg-surface border border-surface-border rounded-xl text-sm text-text-secondary hover:border-primary-100 hover:bg-primary-50 transition-all duration-200 text-left"
                    >
                      <s.icon size={18} className={s.color} strokeWidth={1.5} />
                      {s.text}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`mb-5 flex flex-col ${msg.role === "user" ? "items-end" : "items-start"}`}>
                {/* User uploaded images — outside the bubble */}
                {msg.role === "user" && msg.imageUrls?.length > 0 && (
                  <div className="mb-2 flex gap-2 flex-wrap justify-end">
                    {msg.imageUrls.map((url, j) => (
                      <img
                        key={j}
                        src={url}
                        alt=""
                        className="max-w-[200px] max-h-[200px] rounded-xl object-cover cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setLightboxUrl(url)}
                      />
                    ))}
                  </div>
                )}
                <div className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  {msg.role === "assistant" && (
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                      <img src="/logo.png" alt="Beauly" className="w-7 h-7 object-contain" />
                    </div>
                  )}
                  <div
                    className={`max-w-[85%] md:max-w-[85%] ${
                      msg.role === "user"
                        ? "bg-primary text-white rounded-2xl rounded-br-md px-4 py-3"
                        : "text-text-primary"
                    }`}
                  >
                    {msg.role === "assistant" ? (
                      <div className="prose prose-sm max-w-none prose-headings:text-primary prose-p:text-text-secondary prose-strong:text-primary prose-a:text-accent-dark">
                        {msg.imageUrl && (
                          <img
                            src={msg.imageUrl}
                            alt={t("chat_generated")}
                            className="rounded-xl mb-3 max-w-full cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => setLightboxUrl(msg.imageUrl)}
                          />
                        )}
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                      </div>
                    ) : (
                      <div>{msg.content}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Streaming message — rendered separately for performance */}
            {isStreaming && (
              <div className="mb-5 flex justify-start">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                  <img src="/logo.png" alt="Beauly" className="w-7 h-7 object-contain" />
                </div>
                <div className="max-w-[85%] md:max-w-[85%] text-text-primary">
                  <div className="prose prose-sm max-w-none prose-headings:text-primary prose-p:text-text-secondary prose-strong:text-primary prose-a:text-accent-dark">
                    {streamingMeta?.imageGenerating && !streamingMeta?.imageUrl && (
                      <div className="flex items-center gap-3 mb-3 py-3 px-4 bg-primary-50 rounded-xl">
                        <div className="animate-spin w-4 h-4 border-2 border-primary-200 border-t-accent-dark rounded-full" />
                        <span className="text-sm text-accent-dark font-medium">{t("chat_generating")}</span>
                      </div>
                    )}
                    {streamingMeta?.imageUrl && (
                      <img
                        src={streamingMeta.imageUrl}
                        alt={t("chat_generated")}
                        className="rounded-xl mb-3 max-w-full cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setLightboxUrl(streamingMeta.imageUrl)}
                      />
                    )}
                    {!streamingContent ? (
                      <div className="flex gap-1.5 py-2">
                        <div className="w-2 h-2 bg-primary-300 rounded-full animate-bounce" />
                        <div className="w-2 h-2 bg-primary-300 rounded-full animate-bounce [animation-delay:0.1s]" />
                        <div className="w-2 h-2 bg-primary-300 rounded-full animate-bounce [animation-delay:0.2s]" />
                      </div>
                    ) : (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamingContent}</ReactMarkdown>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* File Preview */}
        {selectedFiles.length > 0 && (
          <div className="max-w-4xl mx-auto w-full px-4 pb-2 flex gap-2 flex-wrap">
            {selectedFiles.map((f, i) => (
              <div key={i} className="relative group">
                {f.fileType === "image" ? (
                  <div className="relative">
                    <img
                      src={f.previewUrl || `data:${f.type};base64,${f.data}`}
                      alt={f.name}
                      className="h-16 w-16 object-cover rounded-lg"
                    />
                    <button
                      onClick={() => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); setSelectedFiles((prev) => prev.filter((_, j) => j !== i)); }}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={10} />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 bg-primary-50 border border-primary-100 rounded-lg px-3 py-1.5 text-sm text-primary">
                    <Paperclip size={12} className="text-accent-dark" />
                    <span className="truncate max-w-[150px]">{f.name}</span>
                    <button
                      onClick={() => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); setSelectedFiles((prev) => prev.filter((_, j) => j !== i)); }}
                      className="text-text-muted hover:text-red-500 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="flex-shrink-0 px-4 pb-5 pt-2">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-end gap-2 bg-surface border border-surface-borderDark rounded-2xl px-4 py-3 focus-within:border-accent transition-colors">
              <input type="file" ref={fileInputRef} className="hidden" accept="image/png,image/jpeg,image/jpg,image/webp,image/gif" multiple onChange={handleFileSelect} />

              <button
                onClick={() => fileInputRef.current?.click()}
                className="text-text-muted hover:text-accent-dark transition-colors p-1"
                title={t("chat_upload")}
              >
                <Paperclip size={20} />
              </button>

              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={t("chat_placeholder")}
                rows={1}
                className="flex-1 resize-none outline-none text-sm max-h-32 bg-transparent text-primary placeholder-text-placeholder leading-relaxed"
              />

              <button
                onClick={handleSend}
                disabled={isStreaming || (!input.trim() && !selectedFiles.length)}
                className="p-2 bg-accent text-white rounded-xl hover:bg-accent-hover transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Send size={16} />
              </button>
            </div>

            <p className="text-center text-[11px] text-text-placeholder mt-3">
              {t("chat_disclaimer")}
            </p>
          </div>
        </div>
      </div>
      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            onClick={() => setLightboxUrl(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
          >
            <X size={28} />
          </button>
          <img
            src={lightboxUrl}
            alt=""
            className="max-w-full max-h-full object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Delete Account — Confirmation */}
      {deleteStep === 1 && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setDeleteStep(0)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg text-primary font-semibold mb-2">
              {t("chat_delete_title") || "Account löschen?"}
            </h3>
            <p className="text-sm text-text-secondary mb-6">
              {t("chat_delete_warning") || "Alle deine Daten, Chatverläufe und Bilder werden unwiderruflich gelöscht. Diese Aktion kann nicht rückgängig gemacht werden."}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteStep(0)}
                className="px-4 py-2 text-sm text-text-secondary hover:text-primary transition-colors"
              >
                {t("chat_delete_cancel") || "Abbrechen"}
              </button>
              <button
                onClick={async () => {
                  try {
                    await deleteAccount();
                  } catch (e) {
                    console.error(e);
                  }
                  setDeleteStep(0);
                }}
                className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                {t("chat_delete_confirm") || "Endgültig löschen"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Camera Modal */}
      {showCamera && (
        <CameraModal
          onCapture={async (imgData) => {
            setShowCamera(false);
            if (onCameraDone) onCameraDone();
            let convId = activeConversation;
            if (!convId) {
              const conv = await createConversation();
              convId = conv?.id;
            }
            const file = {
              name: "photo.jpg",
              type: imgData.type,
              fileType: "image",
              data: imgData.data,
              size: imgData.data.length,
            };
            await sendMessage("Analysiere mein Gesicht und zeige mir mein Potenzial", "edit_image", [file]);
          }}
          onClose={() => { setShowCamera(false); if (onCameraDone) onCameraDone(); }}
        />
      )}

      {/* Paywall Modal */}
      {showPaywall && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowPaywall(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full mx-4 p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
              <CreditCard size={24} className="text-accent-dark" />
            </div>
            <h3 className="font-display text-lg text-primary font-semibold mb-2">
              {t("credits_exhausted_title") || "Credits used up"}
            </h3>
            <p className="text-sm text-text-secondary mb-2">
              {t("credits_exhausted_desc") || "You've used all your credits. Buy more to keep using Beauly."}
            </p>
            <p className="text-xs text-text-muted mb-6">
              {t("credits_exhausted_info") || "10 images + 30 messages per purchase"}
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => { setShowPaywall(false); createCheckout(); }}
                className="w-full py-3 bg-accent text-white rounded-lg font-medium hover:bg-accent-hover transition-colors flex items-center justify-center gap-2"
              >
                <CreditCard size={16} /> {t("credits_buy_now") || "Buy credits now"}
              </button>
              <button
                onClick={() => setShowPaywall(false)}
                className="w-full py-2 text-sm text-text-muted hover:text-primary transition-colors"
              >
                {t("credits_later") || "Later"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
